import { c as _c } from "react/compiler-runtime";
import { useAppSelector } from "@store/store-helper";
import { searchSelector } from "@store/ui/ui-selector";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
/**
 * Manages a search query backed by the shared UI search state, only exposing the
 * search value once it is valid (per `isValidSearch`) or fully cleared. This prevents
 * invalid requests from being sent to the backend while the user is still typing.
 * Text typed on another page is ignored: the search bar only clears it once the new page has
 * rendered, and adopting it in the meantime would send a request with the previous page's search.
 *
 * @param isValidSearch Predicate that returns true when the trimmed search text is valid
 */
export function useValidatedSearchQuery(isValidSearch) {
  const $ = _c(17);
  const {
    pathname
  } = useLocation();
  const {
    debouncedSearchText,
    isDebouncingSearch,
    pathname: searchPathname
  } = useAppSelector(searchSelector);
  const isSearchFromThisPage = searchPathname === pathname;
  let t0;
  if ($[0] !== debouncedSearchText || $[1] !== isSearchFromThisPage) {
    t0 = isSearchFromThisPage ? debouncedSearchText.trim() : "";
    $[0] = debouncedSearchText;
    $[1] = isSearchFromThisPage;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const sanitizedDebouncedSearchText = t0;
  const [search, setSearch] = useState(undefined);
  let t1;
  let t2;
  if ($[3] !== isDebouncingSearch || $[4] !== isValidSearch || $[5] !== sanitizedDebouncedSearchText || $[6] !== search) {
    t1 = () => {
      if (isDebouncingSearch || search === sanitizedDebouncedSearchText) {
        return;
      }
      if (isValidSearch(sanitizedDebouncedSearchText)) {
        return setSearch(sanitizedDebouncedSearchText);
      }
      if (sanitizedDebouncedSearchText.length === 0) {
        return setSearch(undefined);
      }
    };
    t2 = [isDebouncingSearch, sanitizedDebouncedSearchText, search, isValidSearch];
    $[3] = isDebouncingSearch;
    $[4] = isValidSearch;
    $[5] = sanitizedDebouncedSearchText;
    $[6] = search;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  const t3 = sanitizedDebouncedSearchText.length > 0 ? search : undefined;
  let t4;
  if ($[9] !== isDebouncingSearch || $[10] !== isSearchFromThisPage || $[11] !== isValidSearch || $[12] !== sanitizedDebouncedSearchText) {
    t4 = isSearchFromThisPage && isDebouncingSearch && isValidSearch(sanitizedDebouncedSearchText);
    $[9] = isDebouncingSearch;
    $[10] = isSearchFromThisPage;
    $[11] = isValidSearch;
    $[12] = sanitizedDebouncedSearchText;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = {
      search: t3,
      isDebouncingSearch: t4
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}